import React from "react";
import { lazy, Suspense, useCallback, useEffect } from "react";
import { shallowEqual, useDispatch, useSelector } from "react-redux";
import { isUserAuthorized, getCurrentTheme } from "./store/selectors";
import { checkToken, fetchUserProfile, loadInitialData } from "./actions/authActions";
import withClearCache from "./ClearCache";
import { ConfigProvider, theme } from "antd";
import { THEME } from "./constants";
import es_ES from "antd/es/locale/es_ES";
import { getCookie, setCookie } from "./utils";

const MainContainer = lazy(() => import("./containers/MainContainer"));
const AuthContainer = lazy(() => import("./containers/AuthContainer"));

const App = () => {
	const dispatch = useDispatch();
	const currentTheme = useSelector(getCurrentTheme, shallowEqual);

	const ClearCacheComponent = withClearCache(() => {
		return <></>;
	});

	const { auth } = useSelector(isUserAuthorized) || {};

	const fetchInitialData = useCallback(() => {
		dispatch(fetchUserProfile());
		dispatch(loadInitialData());
	}, [dispatch]);

	const checkPersistency = useCallback(() => {
		dispatch(checkToken());
	}, [dispatch]);

	useEffect(() => {
		auth ? fetchInitialData() : checkPersistency();
	}, [auth, fetchInitialData, checkPersistency]);

	useEffect(() => {
		const refreshTheme = async () => {
			const theme = await getCookie(THEME);
			if (theme === undefined || theme === null) {
				await setCookie(THEME, true);
				await dispatch({ type: THEME, payload: true });
			} else {
				await dispatch({ type: THEME, payload: theme });
			}
		};
		refreshTheme();
	}, [dispatch]);

	useEffect(() => {
		document.documentElement.dataset.theme = currentTheme ? "light" : "dark";
	}, [currentTheme]);

	const isLight = Boolean(currentTheme);

	return (
		<Suspense>
			<ConfigProvider
				locale={es_ES}
				theme={{
					token: {
						borderRadius: 10,
						colorPrimary: "#d23c32",
						colorInfo: "#d23c32",
						colorLink: "#d23c32",
						colorSuccess: "#2f9e6b",
						colorWarning: "#e08a1e",
						colorError: "#c4322c",
						fontFamily: '"Charlie Display", "Segoe UI", sans-serif',
						colorBgBase: isLight ? "#fffdfb" : "#171412",
						colorBgLayout: isLight ? "#f3eee7" : "#12100e",
						colorBgContainer: isLight ? "#fffdfb" : "#211e1b",
						colorText: isLight ? "#1c1917" : "#f6f1ea",
						colorTextSecondary: isLight ? "#78716c" : "#a8a29e",
						colorBorder: isLight ? "#e8dfd4" : "#3c352f",
						colorBorderSecondary: isLight ? "#f0e8df" : "#2e2925",
						colorTextDisabled: isLight ? "rgba(28, 25, 23, 0.45)" : "rgba(246, 241, 234, 0.4)",
						controlHeight: 38,
						fontSize: 14,
					},
					components: {
						Switch: {
							colorPrimary: "#d23c32",
							colorPrimaryHover: "#e15a4f",
						},
						Button: {
							primaryShadow: "none",
							fontWeight: 650,
							controlHeight: 38,
						},
						Layout: {
							headerBg: isLight ? "#fffdfb" : "#1c1917",
							siderBg: isLight ? "#fffdfb" : "#1c1917",
							bodyBg: isLight ? "#f3eee7" : "#12100e",
							headerHeight: 72,
							headerPadding: "0 16px",
						},
						Menu: {
							itemBorderRadius: 10,
							itemHeight: 42,
							itemMarginInline: 8,
							iconSize: 16,
							itemSelectedBg: isLight ? "#fdeceb" : "#3d2422",
							itemSelectedColor: isLight ? "#d23c32" : "#f3b2ac",
							itemHoverBg: isLight ? "#f7f1ea" : "#2a2420",
							itemColor: isLight ? "#57534e" : "#d6d3d1",
						},
						Card: {
							borderRadiusLG: 18,
						},
						Table: {
							headerBg: isLight ? "#faf6f1" : "#2a2420",
							headerColor: isLight ? "#78716c" : "#d6d3d1",
							rowHoverBg: isLight ? "#faf6f1" : "#2a2420",
							headerSplitColor: "transparent",
						},
						Modal: {
							borderRadiusLG: 20,
						},
						Input: {
							activeShadow: "0 0 0 3px rgba(210, 60, 50, 0.12)",
						},
					},
					algorithm: isLight ? theme.defaultAlgorithm : theme.darkAlgorithm,
				}}
			>
				<ClearCacheComponent />
				{auth ? <MainContainer /> : <AuthContainer />}
			</ConfigProvider>
		</Suspense>
	);
};

export default App;
